import { memo, useEffect, useState, useRef } from 'react'
import injectSheet from 'react-jss'
import gsap from 'gsap'
import classNames from 'classnames'
import RevealFadeIn from '@/components/RevealFadeIn'
import useMediaQuery from '@/hooks/useMediaQuery'
import { useResize } from '@/components/Handlers'
import useInView from '@/hooks/useInView'
import style from './style'

const ProductDescription = ({
  items,
  image,
  alignText,
  classes,
}) => {
  const [index, setIndex] = useState(0)
  const { match: isMobile } = useMediaQuery('sm')
  const { match: isTablet } = useMediaQuery('md')
  const [textWrapperHeight, setTextWrapperHeight] = useState(0)
  const $text = useRef()
  const $figure = useRef()
  const $nav = useRef()
  const $root = useRef()
  const { isInView } = useInView({
    start: 'top center',
    end: 'bottom 15%',
    once: false,
    trigger: $root.current,
  })

  useResize(() => {
    if ($text.current) {
      const $texts = Array.prototype.slice.call($text.current?.children)
      let maxHeight
      if ($texts.length > 1) {
        maxHeight = $texts.reduce((prev, current) => ((prev?.children[0].offsetHeight > current?.children[0].offsetHeight) ? prev.children[0].offsetHeight : current.children[0].offsetHeight))
      } else {
        maxHeight = $texts[0].children[0].offsetHeight
      }
      setTextWrapperHeight(maxHeight)
    }
  })

  useEffect(() => {
    // Image Animations
    gsap.to($figure.current, {
      y: isInView ? 0 : 100,
      opacity: isInView ? 1 : 0,
      duration: 1.4,
      ease: 'power4.out',
    })
    gsap.to($figure.current.children, {
      scale: isInView ? 1 : 1.1,
      duration: 1.4,
      ease: 'power4.out',
    })

    // Text Animations
    gsap.to($text.current, {
      y: isInView ? 0 : 100,
      opacity: isInView ? 1 : 0,
      duration: 1.4,
      ease: 'power4.out',
    })

    // Nav Animations
    const sign = isTablet ? '+' : '-'
    gsap.to($nav.current.children, {
      y: isInView || isMobile ? 0 : `${sign}100`,
      opacity: isInView ? 1 : 0,
      duration: 1.4,
      stagger: 0.2,
      ease: 'power4.out',
    })
  }, [isInView, isMobile])

  return (
    <div
      className={classNames({
        [classes.root]: true,
        [classes.rootRight]: alignText === 'right',
        [classes.rootLeft]: alignText === 'left',
      })}
      ref={$root}
    >
      <div className={classes.description}>
        <div className={classes.content}>
          <ul
            ref={$nav}
            className={classes.nav}
          >
            {items.map((item, i) => (
              <li
                key={i.toString()}
                className={classNames({
                  [classes.active]: i === index,
                })}
              >
                <button
                  onClick={() => setIndex(i)}
                  aria-label="switch-content"
                >
                  {item.title}
                </button>
              </li>
            ))}
          </ul>
          <div
            className={classes.text}
            ref={$text}
            style={{ height: textWrapperHeight }}
          >
            {items.map((item, i) => (
              <RevealFadeIn
                key={i.toString()}
                visible={i === index}
                exitDuration={0.8}
                className={classNames({
                  [classes.text]: true,
                  [classes.activeText]: i === index,
                })}
              >
                <div dangerouslySetInnerHTML={{ __html: item.text }} />
              </RevealFadeIn>
            ))}
          </div>
        </div>
        <div ref={$figure} className={classes.figure}>
          <div className={classes.image}>
            <img
              src={image.url}
              alt={image.description}
            />
          </div>
        </div>
      </div>
    </div>
  )
}

ProductDescription.defaultProps = {
  alignText: 'left',
  items: [],
  image: {},
}

export default injectSheet(style)(memo(ProductDescription))
